import { useLayoutEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { legacyPageRedirects, type LegacyRedirect } from '@/mocks/legacyRedirects';

/**
 * App-side half of the legacy URL registry.
 *
 * netlify.toml issues real 301s at the edge, but that only helps when the site
 * is actually served by Netlify. This gate gives the same behaviour on any host
 * (the Readdy preview domain, localhost, a future CDN) so an old link never
 * lands on a "Page Not Found" that a crawler would record as a soft 404.
 *
 * It only ever matches exact pathnames — never prefixes — so live routes and
 * article slugs are untouched.
 */
const normalize = (pathname: string): string => {
  const trimmed = pathname.toLowerCase().replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
};

export default function LegacyRedirectGate() {
  const location = useLocation();
  const navigate = useNavigate();
  // Tracks the legacy paths visited during the current redirect burst so a
  // badly configured A->B->A cycle cannot spin forever.
  const visited = useRef<Set<string>>(new Set());

  const lookup = useMemo(() => {
    const map = new Map<string, LegacyRedirect>();
    legacyPageRedirects.forEach((entry) => {
      map.set(normalize(entry.from), entry);
    });
    return map;
  }, []);

  useLayoutEffect(() => {
    const current = normalize(location.pathname);

    if (current === '/') {
      visited.current.clear();
      return;
    }

    const entry = lookup.get(current);

    if (!entry) {
      visited.current.clear();
      return;
    }

    if (visited.current.has(current)) return;
    visited.current.add(current);

    const [targetPath, targetHash] = entry.to.split('#');
    // Prefer the target's own anchor; otherwise carry the visitor's hash over.
    const hash = targetHash ? `#${targetHash}` : location.hash;
    const next = `${targetPath || '/'}${location.search}${hash}`;

    navigate(next, { replace: true });
  }, [location.pathname, location.search, location.hash, lookup, navigate]);

  return null;
}